import { Link } from 'react-router-dom';
import { formatHours, formatPercent, APP_ROLE_META } from '@ssn/shared';
import { useUsers } from '@/lib/queries';
import { PageHeader, Panel } from '@/components/ui/Panel';
import { EmptyState, ErrorState, SkeletonRows } from '@/components/ui/Feedback';
import { TableWrap, Td, Th, Tr } from '@/components/ui/Table';
import { UserChip } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { formatRelativeTime } from '@/lib/format';

/**
 * Developer management (§17).
 *
 * Every figure here is over the last 30 days, and each one is a link into that
 * developer's full profile — the table is for spotting who is overloaded or
 * struggling, not for drawing conclusions from on its own.
 */
export function TeamPage() {
  const { data, isPending, error, refetch } = useUsers();

  return (
    <>
      <PageHeader
        title="Team"
        description="Every developer, their current workload and how the last 30 days have gone."
      />

      <Panel flush>
        {error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : isPending ? (
          <SkeletonRows rows={5} className="p-4" />
        ) : !data || data.users.length === 0 ? (
          <EmptyState
            title="No developers yet"
            description="Developers appear here once they have signed in with Discord and been given a development role."
          />
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <Th>Developer</Th>
                <Th className="w-40">Role</Th>
                <Th align="right">Active</Th>
                <Th align="right">In review</Th>
                <Th align="right">Overdue</Th>
                <Th align="right">Completed</Th>
                <Th align="right">Changes asked</Th>
                <Th align="right">This week</Th>
                <Th align="right">This month</Th>
                <Th align="right">Avg time</Th>
                <Th align="right">Avg review</Th>
                <Th align="right">Approval</Th>
                <Th align="right">First-pass</Th>
                <Th align="right">Last active</Th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((row) => (
                <Tr key={row.user.id}>
                  <Td>
                    <Link to={`/team/${row.user.id}`} className="hover:text-accent-text">
                      <UserChip user={row.user} size="xs" />
                    </Link>
                    {/* Discord identity, distinct from the display name (§17). */}
                    <span className="mt-0.5 block text-[11px] text-text-subtle">@{row.user.username}</span>
                  </Td>
                  <Td>
                    <Badge tone={APP_ROLE_META[row.user.appRole].tone === 'accent' ? 'accent' : 'neutral'} size="sm">
                      {APP_ROLE_META[row.user.appRole].shortLabel}
                    </Badge>
                    {!row.user.isActive && (
                      <Badge tone="muted" size="sm" className="ml-1">
                        Deactivated
                      </Badge>
                    )}
                  </Td>
                  <Td align="right" className="tabular">{row.activeTasks}</Td>
                  <Td align="right" className="tabular">{row.awaitingReview}</Td>
                  <Td
                    align="right"
                    className={row.overdueTasks > 0 ? 'font-medium text-critical-tone tabular' : 'tabular'}
                  >
                    {row.overdueTasks}
                  </Td>
                  <Td align="right" className="tabular">{row.completedTasks}</Td>
                  <Td align="right" className="tabular">{row.changesRequested}</Td>
                  <Td align="right" className="tabular">{row.completedThisWeek}</Td>
                  <Td align="right" className="tabular">{row.completedThisMonth}</Td>
                  <Td align="right" className="tabular">{formatHours(row.averageCompletionMs)}</Td>
                  <Td align="right" className="tabular">{formatHours(row.averageReviewMs)}</Td>
                  <Td align="right" className="tabular">{formatPercent(row.approvalRate)}</Td>
                  <Td align="right" className="tabular">{formatPercent(row.firstPassApprovalRate)}</Td>
                  <Td align="right" className="text-xs text-text-muted">
                    {formatRelativeTime(row.lastActiveAt)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Panel>

      <p className="mt-3 text-[11px] text-text-subtle">
        Averages over the last 30 days. A rate calculated from very few tasks is not a track record —
        open a developer's profile to see the sample sizes behind these figures.
      </p>
    </>
  );
}
